/**
 * MOSA-697 item 19. What the link field says while the user types.
 *
 * The wizard checked the link only when the user pressed the button, so a
 * wrong link looked accepted until then. This module answers the same question
 * one keystroke at a time, and it stays pure: no React, no network. The server
 * link check is still a separate step, because only the server knows whether
 * the file exists.
 *
 * Three tones:
 *   idle  — the text can still become a link. Say nothing.
 *   ok    — the parser accepts the link.
 *   error — the parser refuses it, with the reason from `sheets-link.ts`.
 */

import {
  parseSheetsLink,
  sheetsLinkMessage,
  type SheetsLinkFailure,
} from "./sheets-link";

export type SheetsLinkFieldTone = "idle" | "ok" | "error";

export type SheetsLinkFieldState = {
  tone: SheetsLinkFieldTone;
  /** The line under the field. Empty on `idle`. */
  message: string;
  /** Set on `error`, for the test that maps a reason to its message. */
  reason?: SheetsLinkFailure;
};

/** The paths a pasted Google link starts with, without the scheme. */
const LINK_STARTS = [
  "docs.google.com/spreadsheets/d/",
  "docs.google.com/spreadsheets/u/0/d/",
  "docs.google.com/u/0/spreadsheets/d/",
  "drive.google.com/file/d/",
  "drive.google.com/open?id=",
];

/** A spreadsheet id is 20 characters or more, so a shorter one is unfinished. */
const PARTIAL_ID = /^[A-Za-z0-9_-]{1,19}$/;

const escapeForRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** One of the starts above, and an id the user has not finished typing. */
const PARTIAL_LINK = new RegExp(
  `^(?:https?://)?(?:${LINK_STARTS.map(escapeForRegExp).join("|")})[A-Za-z0-9_-]*$`,
  "i",
);

const withoutScheme = (text: string) => text.replace(/^https?:\/\//i, "");

/**
 * True while the text can still become a link. "https://docs.goo" is one of
 * these; "https://example.com/x" is not, and gets its error at once.
 */
function stillTyping(text: string): boolean {
  if (!text) return true;
  if (PARTIAL_ID.test(text)) return true;
  if (PARTIAL_LINK.test(text)) return true;
  const lower = text.toLowerCase();
  // The scheme on its own: "https:" is not yet a host to judge.
  if (["https://", "http://"].some((scheme) => scheme.startsWith(lower))) {
    return true;
  }
  const head = withoutScheme(lower);
  return LINK_STARTS.some((start) => start.startsWith(head));
}

export function sheetsLinkFieldState(
  raw: string | null | undefined,
): SheetsLinkFieldState {
  const text = (raw ?? "").trim();
  const parsed = parseSheetsLink(text);

  if (parsed.ok) {
    return {
      tone: "ok",
      message:
        parsed.kind === "drive_file"
          ? "This is a Drive file link. OnlyDash reads it if it is a Google Sheet."
          : "This is a Google Sheet link.",
    };
  }

  if (stillTyping(text)) {
    return { tone: "idle", message: "" };
  }

  return {
    tone: "error",
    message: sheetsLinkMessage(parsed.reason),
    reason: parsed.reason,
  };
}
